import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectorRef
} from '@angular/core';
import { PDFDocument, degrees } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { inject } from '@angular/core';
import { PdfjsWorkerBootstrapService } from 'src/app/services/pdfjs-worker-bootstrap.service';
import { MessageService } from 'primeng/api';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-document-preview-modal',
  templateUrl: './document-preview-modal.component.html',
  styleUrls: ['./document-preview-modal.component.scss'],
  standalone: false
})
export class DocumentPreviewModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible: boolean = false;
  @Input() file: File | null = null;
  @Input() files: File[] = [];
  @Input() pdfSrc: any = null;
  @Input() fileName: string = '';
  @Input() fileType: string = 'pdf'; // 'pdf' | 'image' | 'template'
    private _isTemplate: boolean = false;
  @Input()
  set isTemplate(val: boolean) {
    this._isTemplate = !!val;
    this.cdr.detectChanges();
  }
  get isTemplate(): boolean {
    return this._isTemplate;
  }
  @Input() mode: 'upload' | 'preview' = 'preview';
  @Input() showChangeButton: boolean = false;
  @Input() htmlContent: string | null = null;
  @Input() imageUrl: string | null = null;
  @Input() maxFileSizeMB: number = 15;
  @Input() multiple: boolean = true;

  get canChangeFile(): boolean {
    if (this.isTemplate) return false;
    if (this.fileType === 'template') return false;
    return true;
  }

  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() onUpload = new EventEmitter<{ file: File; files?: File[]; base64?: string; arrayBuffer?: ArrayBuffer }>();
  @Output() onFileChange = new EventEmitter<File | File[]>();
  @Output() onSave = new EventEmitter<{ file: File; files?: File[]; base64?: string; arrayBuffer?: ArrayBuffer }>();
  @Output() onCancel = new EventEmitter<void>();

  @ViewChild('fileChangeInput') fileChangeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('previewCanvas') previewCanvas?: ElementRef<HTMLCanvasElement>;
  pageNumbers: number[] = [];
  private pdfjsWorkerBootstrap = inject(PdfjsWorkerBootstrapService);
  private loadedPdfDoc: any = null;
  private activeRenderTasks: any[] = [];
  private loadSessionId: number = 0;
  private isInternalFileChange: boolean = false;
  private isUploadConfirmed: boolean = false;
  hasError: boolean = false;

  currentPage: number = 1;
  totalPages: number = 1;
  zoomLevel: number = 1.0;
  hasLandscapePages: boolean = false;
  landscapePageCount: number = 0;
  isRotatedSuccessfully: boolean = false;
  showLandscapeUploadPrompt: boolean = false;
  pageOrientations: ('landscape' | 'portrait')[] = [];
  isRotating: boolean = false;
  isLoading: boolean = false;
  hasChanges: boolean = false;

  currentPdfBytes: Uint8Array | null = null;
  activeViewerSrc: any = null;
  safeTemplateHtml: SafeHtml | null = null;
  private createdBlobUrl: string | null = null;
  private originalFiles: File[] = []; // tracks the individual files as passed in, before any merge

  constructor(
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['isVisible'] && this.isVisible) {
      this.resetState();
      this.updateSafeTemplateHtml();
      await this.loadDocument();
      this.cdr.detectChanges();
    } else if (
      this.isVisible &&
      (changes['file'] || changes['files'] || changes['pdfSrc'] || changes['imageUrl'] || changes['htmlContent'] || changes['fileType'] || changes['isTemplate'])
    ) {
      if (changes['htmlContent'] || changes['fileType']) {
        this.updateSafeTemplateHtml();
      }
      if (this.isInternalFileChange) {
        this.isInternalFileChange = false;
        return;
      }
      if (changes['file'] && changes['file'].currentValue === this.file && this.currentPdfBytes) {
        return;
      }
      await this.loadDocument();
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.cancelActiveRenderTasks();
    if (this.loadedPdfDoc) {
      try {
        this.loadedPdfDoc.destroy();
      } catch {}
      this.loadedPdfDoc = null;
    }
    this.cleanupBlobUrl();
  }

  private resetState(): void {
    this.isUploadConfirmed = false;
    this.currentPage = 1;
    this.totalPages = 1;
    this.zoomLevel = 1.0;
    this.hasLandscapePages = false;
    this.landscapePageCount = 0;
    this.isRotatedSuccessfully = false;
    this.showLandscapeUploadPrompt = false;
    this.pageOrientations = [];
    this.isRotating = false;
    this.hasChanges = false;
    this.isInternalFileChange = false;
    this.currentPdfBytes = null;
    this.originalFiles = [];
    
    this.safeTemplateHtml = null;
    this.cancelActiveRenderTasks();
    if (this.loadedPdfDoc) {
      try {
        this.loadedPdfDoc.destroy();
      } catch {}
      this.loadedPdfDoc = null;
    }
    this.cleanupBlobUrl();
  }

  private cancelActiveRenderTasks(): void {
    for (const task of this.activeRenderTasks) {
      try {
        task.cancel();
      } catch {}
    }
    this.activeRenderTasks = [];
  }

  private cleanupBlobUrl(): void {
    if (this.createdBlobUrl) {
      URL.revokeObjectURL(this.createdBlobUrl);
      this.createdBlobUrl = null;
    }
  }

  private updateSafeTemplateHtml(): void {
    if (this.fileType === 'template' && this.htmlContent) {
      let content = this.htmlContent;

      const legalStyles = `
        <style id="legal-document-styles">
          html, body {
            margin: 0;
            padding: 0;
            background-color: #ffffff !important;
            color: #111111 !important;
            font-family: 'Times New Roman', Times, Georgia, serif !important;
            font-size: 14px !important;
            line-height: 1.65 !important;
            -webkit-font-smoothing: antialiased;
          }
          body {
            padding: 36px 48px !important;
            box-sizing: border-box !important;
            zoom: 0.88 !important;
          }
          h1, h2, h3, h4, h5, h6, [class*="title"], [class*="header"] {
            font-family: 'Times New Roman', Times, Georgia, serif !important;
            color: #111111 !important;
            font-weight: 700 !important;
          }
          h1 {
            font-size: 20px !important;
            text-align: center !important;
            margin: 18px 0 14px 0 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
          }
          h2 {
            font-size: 16px !important;
            text-align: center !important;
            margin: 16px 0 10px 0 !important;
          }
          h3, h4 {
            font-size: 14.5px !important;
            text-align: left !important;
            margin: 16px 0 8px 0 !important;
            text-transform: uppercase !important;
          }
          p, div, span, li {
            font-family: 'Times New Roman', Times, Georgia, serif !important;
            font-size: 14px !important;
            line-height: 1.65 !important;
            color: #111111 !important;
          }
          p {
            text-align: justify !important;
            text-justify: inter-word !important;
            margin: 0 0 12px 0 !important;
          }
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin: 16px 0 !important;
          }
          th, td {
            border: 1px solid #333333 !important;
            padding: 6px 10px !important;
            font-family: 'Times New Roman', Times, Georgia, serif !important;
            font-size: 13.5px !important;
          }
        </style>
      `;

      if (/<head[^>]*>/i.test(content)) {
        content = content.replace(/<head[^>]*>/i, `$&${legalStyles}`);
      } else if (/<html[^>]*>/i.test(content)) {
        content = content.replace(/<html[^>]*>/i, `$&<head>${legalStyles}</head>`);
      } else {
        content = `<!DOCTYPE html><html><head><meta charset="utf-8">${legalStyles}</head><body>${content}</body></html>`;
      }

      this.safeTemplateHtml = this.sanitizer.bypassSecurityTrustHtml(content);
    } else {
      this.safeTemplateHtml = null;
    }
    this.cdr.detectChanges();
  }



  async loadDocument(): Promise<void> {
    if (this.fileType === 'image' || this.fileType === 'template') {
      return;
    }

    // Snapshot the individual files before any merge so we can restore them on save
    if (this.files && this.files.length > 0 && this.originalFiles.length === 0) {
      this.originalFiles = [...this.files];
    }

    const currentSession = ++this.loadSessionId;
    this.cancelActiveRenderTasks();
    if (this.loadedPdfDoc) {
      try {
        this.loadedPdfDoc.destroy();
      } catch {}
      this.loadedPdfDoc = null;
    }

    this.isLoading = true;
    this.hasError = false;
    this.pageNumbers = [];
    this.cdr.detectChanges();

    try {
      let arrayBuffer: ArrayBuffer | null = null;

      if (this.files && this.files.length > 1) {
        const mergedPdf = await PDFDocument.create();
        for (const f of this.files) {
          const ab = await f.arrayBuffer();
          const doc = await PDFDocument.load(ab, { ignoreEncryption: true });
          const pages = await mergedPdf.copyPages(doc, doc.getPageIndices());
          pages.forEach((p) => mergedPdf.addPage(p));
        }
        const mergedBytes = await mergedPdf.save();
        arrayBuffer = mergedBytes.buffer as ArrayBuffer;
        if (!this.fileName || this.fileName === 'Selected File') {
          this.fileName = this.files.map(f => f.name).join(', ');
        }
      } else if (this.file instanceof File) {
        if (!this.fileName) {
          this.fileName = this.file.name;
        }
        arrayBuffer = await this.file.arrayBuffer();
      } else if (this.files && this.files.length === 1) {
        const singleFile = this.files[0];
        this.file = singleFile;
        if (!this.fileName) {
          this.fileName = singleFile.name;
        }
        arrayBuffer = await singleFile.arrayBuffer();
      } else if (this.pdfSrc) {
        if (typeof this.pdfSrc === 'string') {
          if (this.pdfSrc.startsWith('data:')) {
            const base64Data = this.pdfSrc.split(',')[1];
            const binary = atob(base64Data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            arrayBuffer = bytes.buffer;
          } else if (this.pdfSrc.startsWith('blob:') || this.pdfSrc.startsWith('http')) {
            const response = await fetch(this.pdfSrc);
            arrayBuffer = await response.arrayBuffer();
          } else {
            const binary = atob(this.pdfSrc);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            arrayBuffer = bytes.buffer;
          }
        } else if (this.pdfSrc instanceof Uint8Array) {
          arrayBuffer = (this.pdfSrc.buffer as ArrayBuffer).slice(
            this.pdfSrc.byteOffset,
            this.pdfSrc.byteOffset + this.pdfSrc.byteLength
          );
        } else if (this.pdfSrc instanceof ArrayBuffer) {
          arrayBuffer = this.pdfSrc;
        }
      }

      if (currentSession !== this.loadSessionId) {
        return;
      }

      if (arrayBuffer) {
        this.currentPdfBytes = new Uint8Array(arrayBuffer);
        this.loadedPdfDoc = null;
        await this.analyzeOrientation(this.currentPdfBytes.slice());
        if (currentSession !== this.loadSessionId) return;

        this.pageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        this.cdr.detectChanges();
        setTimeout(() => {
          if (currentSession === this.loadSessionId) {
            this.renderAllPages();
          }
        }, 80);
      } else {
        this.hasError = true;
      }
    } catch (error) {
      if (currentSession === this.loadSessionId) {
        console.error('Error loading PDF in preview modal:', error);
        this.hasError = true;
        this.messageService.add({
          severity: 'error',
          summary: 'Preview Error',
          detail: 'Unable to render PDF preview.'
        });
      }
    } finally {
      if (currentSession === this.loadSessionId) {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  async renderAllPages(): Promise<void> {
    const currentSession = this.loadSessionId;
    if (!this.currentPdfBytes || this.currentPdfBytes.byteLength === 0) return;
    this.isLoading = true;
    this.cdr.detectChanges();

    try {
      this.cancelActiveRenderTasks();
      await this.pdfjsWorkerBootstrap.ready;
      if (currentSession !== this.loadSessionId) return;

      if (!this.loadedPdfDoc) {
        this.loadedPdfDoc = await pdfjsLib.getDocument({ data: this.currentPdfBytes.slice() }).promise;
        if (currentSession !== this.loadSessionId) return;
        this.totalPages = this.loadedPdfDoc.numPages;
        this.pageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        this.cdr.detectChanges();
        await new Promise((resolve) => setTimeout(resolve, 80));
        if (currentSession !== this.loadSessionId) return;
      }

      const dpr = window.devicePixelRatio || 1;
      const baseTargetWidth = 580 * (this.zoomLevel || 1.0);

      if (this.scrollContainer?.nativeElement && this.currentPage === 1) {
        this.scrollContainer.nativeElement.scrollTop = 0;
      }
      for (let p = 1; p <= this.totalPages; p++) {
        if (currentSession !== this.loadSessionId) return;
        const page = await this.loadedPdfDoc.getPage(p);
        if (currentSession !== this.loadSessionId) return;

        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = (baseTargetWidth * dpr) / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        let canvas = document.getElementById('canvas-page-' + p) as HTMLCanvasElement;
        if (!canvas) {
          await new Promise((r) => setTimeout(r, 60));
          if (currentSession !== this.loadSessionId) return;
          canvas = document.getElementById('canvas-page-' + p) as HTMLCanvasElement;
        }

        if (canvas) {
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${Math.round(viewport.width / dpr)}px`;
          canvas.style.height = `${Math.round(viewport.height / dpr)}px`;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const renderTask = page.render({ canvasContext: ctx, viewport });
            this.activeRenderTasks.push(renderTask);
            try {
              await renderTask.promise;
            } catch (taskErr: any) {
              if (taskErr?.name === 'RenderingCancelledException') {
                return;
              }
              throw taskErr;
            } finally {
              const idx = this.activeRenderTasks.indexOf(renderTask);
              if (idx !== -1) {
                this.activeRenderTasks.splice(idx, 1);
              }
            }
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'RenderingCancelledException') {
        return;
      }
      if (currentSession === this.loadSessionId) {
        console.error('Error rendering all pages:', err);
        this.hasError = true;
      }
    } finally {
      if (currentSession === this.loadSessionId) {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  onContainerScroll(event: Event): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container || this.totalPages <= 1) return;

    if (container.scrollTop <= 20) {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        this.cdr.detectChanges();
      }
      return;
    }

    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 20) {
      if (this.currentPage !== this.totalPages) {
        this.currentPage = this.totalPages;
        this.cdr.detectChanges();
      }
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    const probeY = containerTop + 100;
    let activePage = 1;

    for (let i = 1; i <= this.totalPages; i++) {
      const el = document.getElementById('preview-page-' + i);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= probeY && rect.bottom > probeY) {
          activePage = i;
          break;
        }
      }
    }

    if (this.currentPage !== activePage) {
      this.currentPage = activePage;
      this.cdr.detectChanges();
    }
  }

  scrollToPage(pageNum: number): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;
    if (pageNum <= 1) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      this.currentPage = 1;
      this.cdr.detectChanges();
      return;
    }
    const el = document.getElementById('preview-page-' + pageNum);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.currentPage = pageNum;
      this.cdr.detectChanges();
    }
  }

  private async analyzeOrientation(pdfBytes: Uint8Array): Promise<void> {
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      this.totalPages = pages.length;

      let foundLandscape = false;
      let count = 0;
      this.pageOrientations = [];

      pages.forEach((page, idx) => {
        const { width, height } = page.getSize();
        const rot = page.getRotation().angle;
        const isSwapped = rot === 90 || rot === 270;
        const effW = isSwapped ? height : width;
        const effH = isSwapped ? width : height;
        if (effW > effH) {
          foundLandscape = true;
          count++;
          this.pageOrientations[idx] = 'landscape';
        } else {
          this.pageOrientations[idx] = 'portrait';
        }
      });

      this.hasLandscapePages = foundLandscape;
      this.landscapePageCount = count;
      if (!foundLandscape) {
        this.showLandscapeUploadPrompt = false;
      }
    } catch (err) {
      console.error('PDF orientation analysis failed:', err);
      this.hasLandscapePages = false;
      this.landscapePageCount = 0;
      this.pageOrientations = [];
    }
  }

  private updateViewerSource(): void {
    if (!this.currentPdfBytes) return;
    this.cleanupBlobUrl();
    const blob = new Blob([this.currentPdfBytes as any], { type: 'application/pdf' });
    this.createdBlobUrl = URL.createObjectURL(blob);
    this.activeViewerSrc = this.createdBlobUrl;
  }

  onPdfLoaded(pdfInfo: any): void {
    if (pdfInfo && pdfInfo.numPages) {
      this.totalPages = pdfInfo.numPages;
    }
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  toggleZoom(): void {
    if (this.zoomLevel === 1.0) {
      this.zoomLevel = 1.25;
    } else if (this.zoomLevel === 1.25) {
      this.zoomLevel = 1.5;
    } else {
      this.zoomLevel = 1.0;
    }
    this.renderAllPages();
  }

  async rotateLandscapePages(): Promise<void> {
    if (!this.hasLandscapePages || !this.currentPdfBytes || this.isRotating) {
      return;
    }

    this.isRotating = true;
    try {
      // ── Multi-file mode: rotate each original file individually ──────────────
      if (this.originalFiles.length > 1) {
        const rotatedFiles: File[] = [];

        for (const origFile of this.originalFiles) {
          const ab = await origFile.arrayBuffer();
          const pdfDoc = await PDFDocument.load(new Uint8Array(ab), { ignoreEncryption: true });
          const pages = pdfDoc.getPages();
          let modified = false;

          pages.forEach((page) => {
            const { width, height } = page.getSize();
            const currentRot = page.getRotation().angle;
            const isSwapped = currentRot === 90 || currentRot === 270;
            const effW = isSwapped ? height : width;
            const effH = isSwapped ? width : height;
            if (effW > effH) {
              page.setRotation(degrees((currentRot + 90) % 360));
              modified = true;
            }
          });

          if (modified) {
            const savedBytes = await pdfDoc.save();
            const blob = new Blob([savedBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
            rotatedFiles.push(new File([blob], origFile.name, { type: 'application/pdf', lastModified: Date.now() }));
          } else {
            rotatedFiles.push(origFile);
          }
        }

        // Update originalFiles so subsequent saves use the rotated versions
        this.originalFiles = rotatedFiles;
        this.files = rotatedFiles;

        // Rebuild the merged view from the rotated individual files
        const mergedPdf = await PDFDocument.create();
        for (const f of rotatedFiles) {
          const ab = await f.arrayBuffer();
          const doc = await PDFDocument.load(ab, { ignoreEncryption: true });
          const pages = await mergedPdf.copyPages(doc, doc.getPageIndices());
          pages.forEach((p) => mergedPdf.addPage(p));
        }
        const mergedBytes = await mergedPdf.save();
        this.currentPdfBytes = mergedBytes;
        this.loadedPdfDoc = null;
        this.hasChanges = true;
        this.isRotatedSuccessfully = true;

        await this.analyzeOrientation(this.currentPdfBytes.slice());
        await this.renderAllPages();

        this.messageService.add({
          severity: 'success',
          summary: 'Rotated',
          detail: 'Landscape pages rotated to portrait.'
        });
        return;
      }

      // ── Single-file mode ─────────────────────────────────────────────────────
      let bytesToLoad = this.currentPdfBytes;
      if (!bytesToLoad || bytesToLoad.byteLength === 0) {
        if (this.file instanceof File) {
          const ab = await this.file.arrayBuffer();
          this.currentPdfBytes = new Uint8Array(ab);
          bytesToLoad = this.currentPdfBytes;
        }
      }
      if (!bytesToLoad || bytesToLoad.byteLength === 0) {
        throw new Error('No PDF bytes available for rotation');
      }

      const pdfDoc = await PDFDocument.load(bytesToLoad.slice(), { ignoreEncryption: true });
      const pages = pdfDoc.getPages();
      let modified = false;

      pages.forEach((page) => {
        const { width, height } = page.getSize();
        const currentRot = page.getRotation().angle;
        const isSwapped = currentRot === 90 || currentRot === 270;
        const effW = isSwapped ? height : width;
        const effH = isSwapped ? width : height;

        if (effW > effH) {
          page.setRotation(degrees((currentRot + 90) % 360));
          modified = true;
        }
      });

      if (modified) {
        const savedBytes = await pdfDoc.save();
        this.currentPdfBytes = savedBytes;
        this.loadedPdfDoc = null;
        this.hasChanges = true;

        const finalFileName = this.fileName || 'rotated_document.pdf';
        const blob = new Blob([savedBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
        const updatedFile = new File([blob], finalFileName, { type: 'application/pdf', lastModified: Date.now() });
        this.isInternalFileChange = true;
        this.file = updatedFile;
        this.files = [updatedFile];
        this.originalFiles = [updatedFile];

        // Don't emit onSave here — saveAndClose() will emit once when the modal closes.
        this.isRotatedSuccessfully = true;
        await this.analyzeOrientation(this.currentPdfBytes.slice());
        await this.renderAllPages();

        this.messageService.add({
          severity: 'success',
          summary: 'Rotated',
          detail: 'Landscape pages rotated to portrait.'
        });
      }
    } catch (error) {
      console.error('Error rotating PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Rotation Failed',
        detail: 'Could not rotate document.'
      });
    } finally {
      this.isRotating = false;
      this.cdr.detectChanges();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.scrollToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.scrollToPage(this.currentPage + 1);
    }
  }

  triggerChangeFile(): void {
    if (this.fileChangeInput) {
      this.fileChangeInput.nativeElement.value = '';
      this.fileChangeInput.nativeElement.click();
    }
  }

  async onNewFileSelected(event: any): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const selectedFiles: File[] = Array.from(input.files);
    const maxBytes = this.maxFileSizeMB * 1024 * 1024;

    if (selectedFiles.some(f => f.size > maxBytes)) {
      this.messageService.add({
        severity: 'error',
        summary: 'File Too Large',
        detail: `File exceeds maximum limit of ${this.maxFileSizeMB}MB.`
      });
      return;
    }

    if (this.fileType === 'image') {
      const validImg = selectedFiles.every(f =>
        ['image/jpeg', 'image/jpg', 'image/png'].includes(f.type) ||
        ['.jpg', '.jpeg', '.png'].some(ext => f.name.toLowerCase().endsWith(ext))
      );
      if (!validImg) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid File Type',
          detail: 'Only JPG and PNG images are supported.'
        });
        return;
      }
      this.isInternalFileChange = true;
      this.file = selectedFiles[0];
      this.files = [selectedFiles[0]];
      this.fileName = selectedFiles[0].name;
      this.imageUrl = URL.createObjectURL(selectedFiles[0]);
      this.hasChanges = true;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = btoa(e.target.result);
        this.onSave.emit({
          file: selectedFiles[0],
          files: [selectedFiles[0]],
          base64
        });
        this.onFileChange.emit(selectedFiles[0]);
        this.cdr.detectChanges();
      };
      reader.readAsBinaryString(selectedFiles[0]);
      return;
    }

    if (selectedFiles.some(f => !f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Only PDF documents are supported.'
      });
      return;
    }

    this.isInternalFileChange = true;
    if (selectedFiles.length > 1) {
      this.files = selectedFiles;
      this.file = selectedFiles[0];
      this.fileName = selectedFiles.map(f => f.name).join(', ');
    } else {
      this.files = [selectedFiles[0]];
      this.file = selectedFiles[0];
      this.fileName = selectedFiles[0].name;
    }
    this.isRotatedSuccessfully = false;

    this.pdfSrc = null;

    this.hasChanges = true;
    this.currentPage = 1;
    this.loadedPdfDoc = null;
    await this.loadDocument();

    if (this.currentPdfBytes) {
      const finalFileName = this.fileName || (selectedFiles.length > 1 ? selectedFiles.map(f => f.name).join(', ') : selectedFiles[0].name);
      const blob = new Blob([this.currentPdfBytes as any], { type: 'application/pdf' });
      const finalFile = new File([blob], finalFileName, { type: 'application/pdf', lastModified: Date.now() });
      const ab = this.currentPdfBytes.buffer as ArrayBuffer;
    let binary = '';
    const bytes = new Uint8Array(ab);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);

    this.onSave.emit({
      file: finalFile,
      files: selectedFiles,

      base64,
      arrayBuffer: ab
    });
    this.onFileChange.emit(selectedFiles.length === 1 ? selectedFiles[0] : (finalFile as any));
    }
  }

  async handleUpload(): Promise<void> {
    if (!this.currentPdfBytes && !this.file && !this.imageUrl) {
      this.closeModal();
      return;
    }

    // Check if there are unrotated landscape pages
    if (this.fileType === 'pdf' && this.hasLandscapePages && !this.isRotatedSuccessfully) {
      this.showLandscapeUploadPrompt = true;
      this.cdr.detectChanges();
      return;
    }

    await this.completeUpload();
  }

  async confirmRotateAndUpload(): Promise<void> {
    this.showLandscapeUploadPrompt = false;
    await this.rotateLandscapePages();
    await this.completeUpload();
  }

  async confirmUploadAsIs(): Promise<void> {
    this.showLandscapeUploadPrompt = false;
    await this.completeUpload();
  }

  private async completeUpload(): Promise<void> {
    if (!this.currentPdfBytes && !this.file && !this.imageUrl) {
      this.closeModal();
      return;
    }

    this.isUploadConfirmed = true;

    if (this.currentPdfBytes) {
      const finalFileName = this.fileName || 'uploaded_document.pdf';
      const blob = new Blob([this.currentPdfBytes as any], { type: 'application/pdf' });
      const finalFile = new File([blob], finalFileName, { type: 'application/pdf', lastModified: Date.now() });

      // Base64 conversion
      let binary = '';
      const bytes = this.currentPdfBytes;
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      this.onUpload.emit({
        file: finalFile,
        files: this.files && this.files.length > 0 ? this.files : [finalFile],
        base64,
        arrayBuffer: bytes.buffer as ArrayBuffer
      });
    } else if (this.file) {
      this.onUpload.emit({
        file: this.file,
        files: this.files && this.files.length > 0 ? this.files : [this.file]
      });
    }

    this.closeModal();
  }

  async saveAndClose(): Promise<void> {
    if (this.hasChanges && this.currentPdfBytes) {
      if (this.originalFiles.length > 1) {
        // Multi-file mode: emit the individually rotated files back
        // originalFiles was updated in rotateLandscapePages() to hold the rotated versions
        this.onSave.emit({
          file: this.originalFiles[0],
          files: this.originalFiles,
        });
      } else {
        const finalFileName = this.fileName || 'document.pdf';
        const blob = new Blob([this.currentPdfBytes as any], { type: 'application/pdf' });
        const finalFile = new File([blob], finalFileName, { type: 'application/pdf', lastModified: Date.now() });

        let binary = '';
        const bytes = this.currentPdfBytes;
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);

        this.onSave.emit({
          file: finalFile,
          files: this.files && this.files.length > 0 ? this.files : [finalFile],
          base64,
          arrayBuffer: bytes.buffer as ArrayBuffer
        });
      }
    }

    this.closeModal();
  }

  handleCancel(): void {
    this.onCancel.emit();
    this.closeModal();
  }

  handleClose(): void {
    if (this.isUploadConfirmed) {
      return;
    }
    if (this.mode === 'upload') {
      this.handleCancel();
      return;
    }
    if (this.hasChanges && this.mode === 'preview') {
      this.saveAndClose();
    } else {
      this.closeModal();
    }
  }

  private closeModal(): void {
    this.cancelActiveRenderTasks();
    if (this.loadedPdfDoc) {
      try {
        this.loadedPdfDoc.destroy();
      } catch {}
      this.loadedPdfDoc = null;
    }
        this.isVisible = false;
    this.isVisibleChange.emit(false);
    this.cleanupBlobUrl();
  }
}
